import React, { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

/**
 * Non-blocking offline indicator. Renders a slim bar at the top of the
 * viewport when navigator.onLine is false, with a clear message that
 * inventory sync will resume on reconnect. We intentionally do NOT
 * block the UI — the user can still navigate cached pages.
 *
 * The service worker handles the "blank screen when offline" failure
 * mode (serves cached app shell or /offline.html); this component
 * handles the "device went offline mid-session" UX.
 */
export default function OfflineBanner() {
  // `navigator` is undefined during SSR; React's first commit happens
  // in the browser so this is safe, but guard anyway for tests.
  const initial = typeof navigator !== "undefined" ? !navigator.onLine : false;
  const [offline, setOffline] = useState(initial);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const goOffline = () => setOffline(true);
    const goOnline  = () => setOffline(false);
    window.addEventListener("offline", goOffline);
    window.addEventListener("online", goOnline);
    return () => {
      window.removeEventListener("offline", goOffline);
      window.removeEventListener("online", goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      className="fixed top-0 inset-x-0 z-50 bg-ink text-paper px-3 py-2 flex items-center justify-center gap-2 text-[12px] font-mono uppercase tracking-wider shadow-[0_2px_8px_rgba(0,0,0,0.18)]"
    >
      <WifiOff size={14} strokeWidth={2.5} />
      <span>You&apos;re offline · inventory will resume when reconnected</span>
    </div>
  );
}
